const config = require('../../../config-env.json')
const packageInfo = require('../../../package.json')

const ENV = process.env.API_ENV
const configEnv = config[ENV] || {}

let isDatadogInitialized = false
let pendingUser = null

const getRuntimeConfig = () => {
  if (typeof window === 'undefined') {
    return {}
  }

  return window.__DATADOG_CONFIG__ || {}
}

const getConfigValue = (key) => {
  const runtimeValue = getRuntimeConfig()[key]

  if (runtimeValue !== undefined && runtimeValue !== '') {
    return runtimeValue
  }

  return configEnv[key]
}

const toNumber = (value, fallback) => {
  const parsedValue = Number(value)
  return Number.isNaN(parsedValue) ? fallback : parsedValue
}

const toBoolean = (value) => value === true || value === 'true'

const getAllowedTracingUrls = () => {
  const allowedTracingUrls = getConfigValue('DATADOG_ALLOWED_TRACING_URLS')

  if (Array.isArray(allowedTracingUrls)) {
    return allowedTracingUrls
  }

  if (!allowedTracingUrls) {
    return []
  }

  return String(allowedTracingUrls)
    .split(',')
    .map((url) => url.trim())
    .filter(Boolean)
}

const getUserId = (currentUser) =>
  currentUser.username ||
  currentUser.cdLdap ||
  currentUser.ldap ||
  currentUser.dsLogin ||
  currentUser.id

const getDatadogVersion = () =>
  getConfigValue('DATADOG_VERSION') || packageInfo.version || 'unknown'

const canUseDatadog = () =>
  toBoolean(getConfigValue('DATADOG_ENABLED')) &&
  getConfigValue('DATADOG_APPLICATION_ID') &&
  getConfigValue('DATADOG_CLIENT_TOKEN')

const getDatadogCdnUrl = () => {
  const configuredUrl = getConfigValue('DATADOG_CDN_URL')

  if (configuredUrl) {
    return configuredUrl
  }

  const site = getConfigValue('DATADOG_SITE') || 'datadoghq.eu'

  if (site === 'datadoghq.eu') {
    return 'https://www.datadoghq-browser-agent.com/eu1/v7/datadog-rum.js'
  }

  return 'https://www.datadoghq-browser-agent.com/us1/v7/datadog-rum.js'
}

const ensureDatadogRum = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null
  }

  if (window.DD_RUM) {
    return window.DD_RUM
  }

  window.DD_RUM = {
    q: [],
    onReady(callback) {
      this.q.push(callback)
    },
  }

  const script = document.createElement('script')
  script.async = true
  script.src = getDatadogCdnUrl()

  const firstScript = document.getElementsByTagName('script')[0]

  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  return window.DD_RUM
}

const runWhenReady = (callback) => {
  const datadogRum = ensureDatadogRum()

  if (!datadogRum || !datadogRum.onReady) {
    return
  }

  datadogRum.onReady(() => {
    callback(window.DD_RUM)
  })
}

const applyDatadogUser = (datadogRum, currentUser) => {
  if (!datadogRum || !datadogRum.setUser || !currentUser) {
    return
  }

  const userId = getUserId(currentUser)

  if (!userId) {
    return
  }

  datadogRum.setUser({
    id: String(userId),
    branch:
      currentUser.locationCode || currentUser.idBranch || currentUser.cdBranch,
  })
}

export const initDatadog = () => {
  if (!canUseDatadog()) {
    return
  }

  const sessionReplaySampleRate = toNumber(
    getConfigValue('DATADOG_SESSION_REPLAY_SAMPLE_RATE'),
    0
  )

  const datadogOptions = {
    applicationId: getConfigValue('DATADOG_APPLICATION_ID'),
    clientToken: getConfigValue('DATADOG_CLIENT_TOKEN'),
    site: getConfigValue('DATADOG_SITE') || 'datadoghq.eu',
    service: getConfigValue('DATADOG_SERVICE') || 'gestcom-front',
    env: getConfigValue('DATADOG_ENV') || ENV,
    version: getDatadogVersion(),
    sessionSampleRate: toNumber(
      getConfigValue('DATADOG_SESSION_SAMPLE_RATE'),
      100
    ),
    sessionReplaySampleRate,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  }

  const allowedTracingUrls = getAllowedTracingUrls()

  if (allowedTracingUrls.length) {
    datadogOptions.allowedTracingUrls = allowedTracingUrls
  }

  runWhenReady((datadogRum) => {
    try {
      datadogRum.init(datadogOptions)
      isDatadogInitialized = true

      if (
        sessionReplaySampleRate > 0 &&
        datadogRum.startSessionReplayRecording
      ) {
        datadogRum.startSessionReplayRecording()
      }

      applyDatadogUser(datadogRum, pendingUser)
    } catch {
      isDatadogInitialized = false
    }
  })
}

export const setDatadogUser = (currentUser) => {
  pendingUser = currentUser

  if (!canUseDatadog()) {
    return
  }

  runWhenReady((datadogRum) => {
    if (isDatadogInitialized) {
      applyDatadogUser(datadogRum, currentUser)
    }
  })
}

export const clearDatadogUser = () => {
  pendingUser = null

  if (!canUseDatadog()) {
    return
  }

  runWhenReady((datadogRum) => {
    if (isDatadogInitialized && datadogRum.clearUser) {
      datadogRum.clearUser()
    }
  })
}

export default initDatadog
